import { Injectable } from '@angular/core';

import { combineLatest, filter, map, merge, Observable, pairwise, startWith, switchMap, tap } from 'rxjs';

import { makeMap } from '@shared/core';
import { WebSocketEventsEnum } from '@shared/websocket';
import { Account } from '@shared/account/models';
import { InviteWithAccountName } from '@shared/invite/models';
import {
  InviteRolesMap,
  AuthAccountService,
  AuthAccountUserService,
  AuthRestaurantService,
  ProfileInvitesQuery,
  ProfileInvitesStore,
  SocketService,
} from '@tablesready/frontend-store';
import { AccountAccess } from '@tablesready/frontend-status-bar';
import { UserApiService } from '@tablesready/frontend-auth';

@Injectable({ providedIn: 'root' })
export class ProfileInvitesService {
  private accountsMap: Record<string, Account> = {};

  constructor(
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly authAccountService: AuthAccountService,
    private readonly authAccountUserService: AuthAccountUserService,
    private readonly userApiService: UserApiService,
    private readonly query: ProfileInvitesQuery,
    private readonly store: ProfileInvitesStore,
    private readonly socketService: SocketService,
  ) { }

  get(): Observable<AccountAccess[]> {
    return this.authRestaurantService.select()
      .pipe(
        startWith(null),
        pairwise(),
        filter(([prev, current]) => prev === null || prev?.location?._id !== current.location?._id),
        switchMap(() => this.userApiService.getInvites().pipe(tap(invites => this.store.set(invites)))),
        switchMap(() => combineLatest([
          this.authAccountUserService.selectAll(),
          this.query.selectAll() as Observable<InviteWithAccountName[]>,
          this.authAccountService.selectAll().pipe(tap(accounts => this.accountsMap = makeMap(accounts, 'id'))),
        ])),
        map(([accountUsers, invites]) => {
          const accounts: AccountAccess[] = [];
          for (const {id: inviteId, role, accountName, accountId} of invites) {
            accounts.push({
              accountName,
              role: InviteRolesMap[role],
              inviteId,
              fromAccountId: accountId,
            });
          }

          for (const {role, accountId} of accountUsers) {
            const account = this.accountsMap[accountId];
            if (account) {
              accounts.push({accountName: account.name, role, inviteId: null, fromAccountId: null});
            }
          }

          return accounts;
        }),
      );
  }

  listenToChanges() {
    return merge(
      this.socketService.getEventStream<void>(WebSocketEventsEnum.invite)
        .pipe(
          switchMap(() => this.userApiService.getInvites()),
          tap(invites => this.store.set(invites)),
        ),
    );
  }
}
